import { memo, useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { useQuery } from '@apollo/client';
import { Text, TextBlock } from '@library';
import useStoreAndLanguageParams from '../../../core/hooks/useStoreAndLanguageParams';
import type { Product, ProductSibling, ProductVariant } from '../../types';
import FlexLayout from '../../../core/components/layout/FlexLayout';
import productAttributeVariationsQuery from '../../queries/productAttributeVariationsQuery';
import combineAttributes from '../../util/combineAttributes';
import VerticalLayout from '../../../core/components/layout/VerticalLayout';
import HorizontalLayout from '../../../core/components/layout/HorizontalLayout';
import CurrentStoreLink from '../../../core/components/router/CurrentStoreLink';
import notEmpty from '../../../core/util/notEmpty';
import PreorderModeContext from '../../../preorder/PreorderModeContext';
import withErrorBoundary from '../../../core/components/errors/withErrorBoundary';
import SiblingOrVariantItems, {
    getVariationAttributeNames,
    processSiblings,
    ProductSiblingItem,
} from './ProductSiblingOrVariantItems';
import ProductAttributeVariationsModal from './ProductAttributeVariationsModal';
import styles from './ProductDetails.module.scss';

const Modal = memo(
    ({
        product,
        setAttributeVariantsModalOpen,
    }: {
        product: Product;
        setAttributeVariantsModalOpen: (isOpen: boolean) => void;
    }) => {
        return (
            <ProductAttributeVariationsModal
                product={product}
                onClose={() => {
                    setAttributeVariantsModalOpen(false);
                }}
            />
        );
    }
);

const VariantSelector = memo(
    ({
        product,
        pid,
        variants,
        variantId,
        variantNames,
        selectedVariant,
        disabledActions,
        setAttributeVariantsModalOpen,
    }: {
        product: Product;
        pid: string;
        variants?: ProductVariant[];
        variantId?: string;
        variantNames: string[];
        selectedVariant?: ProductVariant;
        disabledActions: boolean;
        setAttributeVariantsModalOpen: (showModal: boolean) => void;
    }) => {
        return !!variants && variants.length > 0 ? (
            <div
                className={styles.productSizesContainer}
                data-test-class="sizeVariant"
            >
                <div className={styles.productSizesTitle}>
                    <TextBlock primary bold>
                        <HorizontalLayout>
                            <VerticalLayout>
                                <SiblingOrVariantItems
                                    names={variantNames}
                                    items={selectedVariant?.variationAttributes}
                                />
                            </VerticalLayout>
                            {product.sizeSpecification && <>&nbsp;|&nbsp;</>}
                            {product.sizeSpecification && (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onKeyPress={() => {
                                        setAttributeVariantsModalOpen(true);
                                    }}
                                    onClick={() => {
                                        setAttributeVariantsModalOpen(true);
                                    }}
                                    className={styles.attributeVariations}
                                >
                                    <FormattedMessage id="size guide" />
                                </div>
                            )}
                        </HorizontalLayout>
                    </TextBlock>
                </div>
                {!disabledActions && (
                    <FlexLayout gap="micro" standardColumns>
                        {variants.map(variant => (
                            <CurrentStoreLink
                                key={variant.id}
                                data-test-class="variantItemSelector"
                                className={clsx(styles.productSizeItem, {
                                    [styles.productSizeItemSelected]:
                                        variant.id === variantId,
                                })}
                                to={`/product/${pid}/${variant.id}`}
                                preventScrollReset
                            >
                                <Text primary>
                                    {
                                        combineAttributes(
                                            variant.variationAttributes
                                        ).value
                                    }
                                </Text>
                            </CurrentStoreLink>
                        ))}
                    </FlexLayout>
                )}
            </div>
        ) : null;
    }
);

const useProductAttributeVariations = ({
    productSiblings,
    variants,
    productSelectedVariant,
    productSiblingAttributes,
}: {
    productSiblings: ProductSibling[];
    variants?: ProductVariant[];
    productSelectedVariant?: ProductVariant;
    productSiblingAttributes?: {
        name: string;
        value: string;
    }[];
}) => {
    const { store, language } = useStoreAndLanguageParams();
    const { productsFilters } = useContext(PreorderModeContext);

    const { pid = '', variantId } = useParams<{
        pid: string;
        variantId?: string;
    }>();
    const {
        data: { product } = {
            product: {} as Product,
        },
        error,
    } = useQuery<{
        product: Product;
    }>(productAttributeVariationsQuery, {
        variables: {
            store,
            language,
            id: pid,
            ...productsFilters,
        },
    });

    if (error) {
        throw error;
    }

    const selectedVariant =
        variants?.find(({ id }) => id === variantId) || productSelectedVariant;

    const processedSiblings = processSiblings(productSiblings, selectedVariant);

    const selectedSibling =
        processedSiblings.find(variant => variant.id === pid)
            ?.siblingAttributes || productSiblingAttributes;

    let siblingNames: string[] = [];

    if (notEmpty(product)) {
        siblingNames = Array.from(
            new Set(product.siblingAttributes?.map(item => item.name))
        );
    }

    const variantNames = getVariationAttributeNames(processedSiblings);

    return {
        product,
        pid,
        processedSiblings,
        selectedSibling,
        siblingNames,
        variantNames,
        selectedVariant,
        variantId,
    };
};

const ProductVariants = ({
    disabledActions = false,
    hasSiblings,
    productSiblings,
    variants,
    productSelectedVariant,
    productSiblingAttributes,
}: {
    disabledActions?: boolean;
    hasSiblings: boolean;
    productSiblings: ProductSibling[];
    variants?: ProductVariant[];
    productSelectedVariant?: ProductVariant;
    productSiblingAttributes?: {
        name: string;
        value: string;
    }[];
}) => {
    const [attributeVariantsModalOpen, setAttributeVariantsModalOpen] =
        useState(false);

    const {
        product,
        pid,
        processedSiblings,
        selectedSibling,
        siblingNames,
        variantNames,
        selectedVariant,
        variantId,
    } = useProductAttributeVariations({
        productSiblings,
        variants,
        productSelectedVariant,
        productSiblingAttributes,
    });

    return (
        <VerticalLayout>
            {hasSiblings && processedSiblings.length > 0 && (
                <div className={styles.productSizes}>
                    {selectedSibling &&
                        (siblingNames.length > 0 ||
                            selectedSibling.length > 0) && (
                            <div className={styles.productSizesTitle}>
                                <TextBlock primary bold>
                                    <VerticalLayout>
                                        <SiblingOrVariantItems
                                            names={siblingNames}
                                            items={selectedSibling}
                                        />
                                    </VerticalLayout>
                                </TextBlock>
                            </div>
                        )}
                    {!disabledActions && (
                        <FlexLayout gap="micro" standardColumns>
                            {processedSiblings.map(sibling => (
                                <ProductSiblingItem
                                    key={sibling.id}
                                    {...sibling}
                                />
                            ))}
                        </FlexLayout>
                    )}
                </div>
            )}
            <VariantSelector
                product={product}
                pid={pid}
                variants={variants}
                variantId={variantId}
                variantNames={variantNames}
                selectedVariant={selectedVariant}
                disabledActions={disabledActions}
                setAttributeVariantsModalOpen={setAttributeVariantsModalOpen}
            />
            {attributeVariantsModalOpen && (
                <Modal
                    product={product}
                    setAttributeVariantsModalOpen={
                        setAttributeVariantsModalOpen
                    }
                />
            )}
        </VerticalLayout>
    );
};

export default memo(withErrorBoundary(ProductVariants));
